<template>
  <vue-date-picker
    :model-value="selectedDate"
    :uid="'date-picker'"
    :locale="globalContent.getLocale"
    :format="format"
    :preview-format="format"
    :min-date="minDate"
    :enable-time-picker="false"
    :clearable="false"
    :disabled-dates="disabledDates"
    :disabled-week-days="disabledWeekDays"
    :space-confirm="false"
    :position="'left'"
    hide-offset-dates
    cancel-text="Close"
    @update:model-value="onSelectDate"
  >
    <template #trigger>
      <div
        class="inline-flex items-center gap-6 px-8 py-6 type-headline-xs text-text-base whitespace-nowrap bg-bg-base border border-border-base hover:bg-bg-subtle hover:border-border-strong cursor-pointer transition-colors"
      >
        <div class="shrink-0">
          <GlobalIcon name="calendar" size="xsmall" />
        </div>
        <div class="shrink-0 whitespace-nowrap"> {{ $t('miniCart.preferredShippingDateShort') + ': ' + displayDate }} </div>
      </div>
    </template>
    <template #day="{ day, date }" :class="getDayClass(date)">
      <div :class="getDayClass(date)">
        {{ day }}
      </div>
      <div v-if="getDayPrice(date) !== null && globalContent.getEnableBreakModeOfDelivery" class="absolute top-36 left-half -translate-x-half type-xxxs text-text-base">
        {{ getDayPrice(date) }}
      </div>
    </template>
    <template #action-extra>
      <div v-if="globalContent.getEnableBreakModeOfDelivery" class="w-full flex items-center justify-start gap-12 type-headline-xs px-8">
        <div class="inline-flex items-center gap-4">
          <div class="w-8 h-8 bg-bg-successStrong rounded-full" />
          <span>{{ $t('checkout.calendar.free') }}</span>
        </div>
        <div v-if="!cartStore.hasDisabledOutsideModFee" class="inline-flex items-center gap-4">
          <div class="w-8 h-8 bg-bg-errorStrong rounded-full" />
          <span>{{ $t('checkout.calendar.outsideMod', { price: outsideMoDPrice }) }}</span>
        </div>
      </div>
    </template>
  </vue-date-picker>
</template>

<script setup lang="ts">
import VueDatePicker from '@vuepic/vue-datepicker';
import { ref } from 'vue';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import '@vuepic/vue-datepicker/dist/main.css';
import { useCartStore } from '~/store/cart';
import { useLocalizedCurrency } from '~/composeables/useLocalizedCurrency';

const globalContent = useGlobalContentStore();
const cartStore = useCartStore();
const userStore = useUserStore();
const { $t } = useNuxtApp();

const props = defineProps<{
  partNo: string,
  date: string,
  defaultDate: string,
  selectedDeliveryPoint?: 'store' | 'address' | 'consumer',
}>();

const emit = defineEmits<{
  (e: 'updatePreferredShippingDate', date: string): void;
}>();

const selectedDate = ref(Date.parse(props.date));

const normalizeToStartOfDay = (date: Date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const parseDateString = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const isoString = value.slice(0, 10);
  const [year, month, day] = isoString.split('-').map(Number);

  if ([year, month, day].some((part) => Number.isNaN(part))) {
    return null;
  }

  return normalizeToStartOfDay(new Date(year, month - 1, day));
};

const orderThresholdDate = computed(() => {
  const now = new Date();
  const offsetDays = Number(globalContent.orderBeforeDays ?? 0);
  const earliest = normalizeToStartOfDay(now);
  earliest.setDate(earliest.getDate() + offsetDays);

  const hasCutoffTime = globalContent.orderBeforeHour !== null && globalContent.orderBeforeHour !== undefined;

  if (hasCutoffTime) {
    const cutoff = new Date(now);
    const cutoffHour = Number(globalContent.orderBeforeHour);
    const cutoffMinute = Number(globalContent.orderBeforeMinute ?? 0);
    cutoff.setHours(cutoffHour, cutoffMinute, 0, 0);

    if (now >= cutoff) {
      earliest.setDate(earliest.getDate() + 1);
    }
  }

  return normalizeToStartOfDay(earliest);
});

const defaultDateValue = computed(() => parseDateString(props.defaultDate));

const firstSelectableDate = computed(() => {
  const orderThreshold = orderThresholdDate.value;

  if (globalContent.getEnableBreakModeOfDelivery) {
    return orderThreshold;
  }

  const defaultDate = defaultDateValue.value;

  if (!defaultDate) {
    return orderThreshold;
  }

  return orderThreshold > defaultDate ? orderThreshold : defaultDate;
});

const minDate = computed(() => {
  const firstSelectable = format(firstSelectableDate.value);

  if (!globalContent.getEnableBreakModeOfDelivery) {
    return firstSelectable;
  }

  const startDate = new Date(firstSelectableDate.value);
  const searchWindowInDays = 365; // practical upper bound for finding an eligible date

  for (let offset = 0; offset <= searchWindowInDays; offset++) {
    const candidate = new Date(startDate);
    candidate.setDate(startDate.getDate() + offset);

    if (getDayClassForMinDate(candidate) === 'day-outside-mod') {
      return format(candidate);
    }
  }

  return firstSelectable;
});

const displayDate = computed(() => {
  if (!selectedDate.value) return '';
  return format(new Date(selectedDate.value));
});

watch(
  () => props.date,
  () => {
    selectedDate.value = Date.parse(props.date);
  }
);
const format = (date: Date) => {
  let day = date.getDate();
  if (day < 10) {
    day = '0'+ day.toString();
  }
  let month = date.getMonth() + 1;
  if (month < 10) {
    month = '0'+ month.toString();
  }
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

const disabledWeekDays = computed(() => {
  if (!globalContent.getEnableBreakModeOfDelivery && userStore.userProfile.company.mod !== '') {
    const allWeekdays = [0,1,2,3,4,5,6];
    const matches = [...userStore.userProfile.company.mod.matchAll(/[1-7]/g)].map((i) => Number(i[0]));
    return allWeekdays.filter(x => !matches.includes(x));
  }

  return [6,0]; // default -> allow mon-fri delivery
});

const disabledWeekDaysReadOnly = computed(() => {
  if (userStore.userProfile.company.mod !== '') {
    const allWeekdays = [0,1,2,3,4,5,6];
    const matches = [...userStore.userProfile.company.mod.matchAll(/[1-7]/g)].map((i) => Number(i[0]));
    return allWeekdays.filter(x => !matches.includes(x));
  }

  return [6,0]; // default -> allow mon-fri delivery
});

function getSwedishRedDays(year: number = new Date().getFullYear()): Date[] {
  const d = (month: number, day: number) => new Date(year, month - 1, day);

  function getEasterDate(y: number): Date {
    const f = Math.floor;
    const G = y % 19;
    const C = f(y / 100);
    const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
    const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
    const J = (y + f(y / 4) + I + 2 - C + f(C / 4)) % 7;
    const L = I - J;
    const month = 3 + f((L + 40) / 44);
    const day = L + 28 - 31 * f(month / 4);
    return new Date(y, month - 1, day);
  }

  function getMidsummerDay(): Date {
    const startDate = new Date(year, 5, 20); // June 20
    for (let i = 0; i <= 6; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      if (date.getDay() === 6) return date;
    }
    throw new Error('Midsummer Day not found');
  }

  function getAllSaintsDay(): Date {
    const startDate = new Date(year, 9, 31); // October 31
    for (let i = 0; i <= 6; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      if (date.getDay() === 6) return date;
    }
    throw new Error('All Saints Day not found');
  }

  const easter = getEasterDate(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);

  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);

  const ascensionDay = new Date(easter);
  ascensionDay.setDate(easter.getDate() + 39);

  const pentecost = new Date(easter);
  pentecost.setDate(easter.getDate() + 49);

  return [
    d(1, 1),         // New Year's Day
    d(1, 6),         // Epiphany
    goodFriday,      // Good Friday
    easter,          // Easter Sunday
    easterMonday,    // Easter Monday
    d(5, 1),         // Labour Day
    ascensionDay,    // Ascension Day
    pentecost,       // Pentecost
    d(6, 6),         // National Day
    getMidsummerDay(), // Midsummer Day (Saturday)
    getAllSaintsDay(), // All Saints' Day (Saturday)
    d(12, 24),       // Christmas Eve (semi-official)
    d(12, 25),       // Christmas Day
    d(12, 26),       // Boxing Day
    d(12, 31),       // New Year's Eve (semi-official)
  ];
}

function getUSHolidays(year: number = new Date().getFullYear()): Date[] {
  const d = (month: number, day: number) => new Date(year, month - 1, day);

  function getEasterDate(y: number): Date {
    const f = Math.floor;
    const G = y % 19;
    const C = f(y / 100);
    const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
    const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
    const J = (y + f(y / 4) + I + 2 - C + f(C / 4)) % 7;
    const L = I - J;
    const month = 3 + f((L + 40) / 44);
    const day = L + 28 - 31 * f(month / 4);
    return new Date(y, month - 1, day);
  }

  // Helper to get nth weekday of month (e.g., 3rd Monday)
  function getNthWeekdayOfMonth(month: number, weekday: number, n: number): Date {
    const firstDay = new Date(year, month - 1, 1);
    const firstWeekday = firstDay.getDay();
    let diff = weekday - firstWeekday;
    if (diff < 0) diff += 7;
    const day = 1 + diff + (n - 1) * 7;
    return new Date(year, month - 1, day);
  }

  // Helper to get last weekday of month
  function getLastWeekdayOfMonth(month: number, weekday: number): Date {
    const lastDay = new Date(year, month, 0); // Last day of month
    const lastWeekday = lastDay.getDay();
    let diff = lastWeekday - weekday;
    if (diff < 0) diff += 7;
    const day = lastDay.getDate() - diff;
    return new Date(year, month - 1, day);
  }

  const holidays: Date[] = [];

  // New Year's Day + adjacent day based on day of week
  const newYearsDay = d(1, 1);
  holidays.push(newYearsDay);
  const newYearsDayOfWeek = newYearsDay.getDay();
  if (newYearsDayOfWeek === 0) { // Sunday
    holidays.push(d(1, 2)); // Monday after
  } else if (newYearsDayOfWeek === 6) { // Saturday
    holidays.push(d(12, 31)); // Friday before (previous year)
  } else if (newYearsDayOfWeek === 2) { // Tuesday - day before is Monday
    const mondayBefore = new Date(newYearsDay);
    mondayBefore.setDate(newYearsDay.getDate() - 1);
    holidays.push(mondayBefore); // December 31 (Monday)
  } else if (newYearsDayOfWeek === 4) { // Thursday
    holidays.push(d(1, 2)); // Friday after
  }

  // MLK Day - 3rd Monday in January
  holidays.push(getNthWeekdayOfMonth(1, 1, 3));

  // President's Day - 3rd Monday in February
  holidays.push(getNthWeekdayOfMonth(2, 1, 3));

  // Good Friday - 2 days before Easter
  const easter = getEasterDate(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  holidays.push(goodFriday);

  // Memorial Day - Last Monday in May
  holidays.push(getLastWeekdayOfMonth(5, 1));

  // Juneteenth - June 19
  holidays.push(d(6, 19));

  // Independence Day - July 4
  holidays.push(d(7, 4));

  // Labor Day - 1st Monday in September
  holidays.push(getNthWeekdayOfMonth(9, 1, 1));

  // Thanksgiving Day - 4th Thursday in November
  holidays.push(getNthWeekdayOfMonth(11, 4, 4));

  // Day after Thanksgiving - Friday after 4th Thursday
  const thanksgiving = getNthWeekdayOfMonth(11, 4, 4);
  const dayAfterThanksgiving = new Date(thanksgiving);
  dayAfterThanksgiving.setDate(thanksgiving.getDate() + 1);
  holidays.push(dayAfterThanksgiving);

  // Christmas Day + adjacent day based on day of week
  const christmasDay = d(12, 25);
  holidays.push(christmasDay);
  const christmasDayOfWeek = christmasDay.getDay();
  if (christmasDayOfWeek === 0) { // Sunday
    holidays.push(d(12, 26)); // Monday after
  } else if (christmasDayOfWeek === 6) { // Saturday
    holidays.push(d(12, 24)); // Friday before
  } else if (christmasDayOfWeek === 2) { // Tuesday - day before is Monday
    holidays.push(d(12, 24)); // Monday before
  } else if (christmasDayOfWeek === 4) { // Thursday
    holidays.push(d(12, 26)); // Friday after
  }

  return holidays;
}

// Select appropriate holiday function based on country
const disabledDates = computed(() => {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  if (globalContent.getCountryCode === 'us') {
    // Include both current and next year holidays
    const holidays = [...getUSHolidays(currentYear), ...getUSHolidays(nextYear)];
    return holidays;
  }
  return [...getSwedishRedDays(currentYear), ...getSwedishRedDays(nextYear)];
});

const getDayPrice = (date: Date) => {
  const dayOfWeek = date.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isHoliday = disabledDates.value.some(
    d => d.toDateString() === date.toDateString()
  );

  // Check if date is in the past
  const isBeforeCutoff = normalizeToStartOfDay(date) < firstSelectableDate.value;

  // Don't show price for disabled dates (weekends, holidays, past)
  if (isWeekend || isHoliday || isBeforeCutoff) {
    return null;
  }

  // Check if this day is outside MoD
  const isOutsideMoD = disabledWeekDaysReadOnly.value.includes(dayOfWeek);

  // When delivery point is 'address', all dates are free (show 0)
  if (globalContent.getEnableBreakModeOfDelivery && props.selectedDeliveryPoint === 'address') {
    return useLocalizedCurrency(0, true);
  }

  if (cartStore.hasDisabledOutsideModFee) {
    return useLocalizedCurrency(0, true);
  }

  // Otherwise: Outside MoD = 150, Inside MoD = 0
  return isOutsideMoD ? useLocalizedCurrency(globalContent.outsideModDeliveryFeePrice as number, true) : useLocalizedCurrency(0, true);
};

const getDayClass = (date: Date) => {
  const dayOfWeek = date.getDay();
  const isOutsideMoD = disabledWeekDaysReadOnly.value.includes(dayOfWeek);
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isHoliday = disabledDates.value.some(
    d => d.toDateString() === date.toDateString()
  );

  const isBeforeCutoff = normalizeToStartOfDay(date) < firstSelectableDate.value;

  if (isWeekend || isHoliday || isBeforeCutoff) {
    return 'day-disabled';
  }

  if (isOutsideMoD && !cartStore.hasDisabledOutsideModFee) {
    return 'day-outside-mod';
  }

  return 'day-inside-mod';
};

const getDayClassForMinDate = (date: Date) => {
  const dayOfWeek = date.getDay();
  const isOutsideMoD = disabledWeekDaysReadOnly.value.includes(dayOfWeek);
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isHoliday = disabledDates.value.some(
    d => d.toDateString() === date.toDateString()
  );

  const isBeforeCutoff = normalizeToStartOfDay(date) < firstSelectableDate.value;

  if (isWeekend || isHoliday || isBeforeCutoff) {
    return 'day-disabled';
  }

  if (isOutsideMoD) {
    return 'day-outside-mod';
  }

  return 'day-inside-mod';
};

const onSelectDate = (modelData: any) => {
  selectedDate.value = modelData;

  emit('updatePreferredShippingDate', selectedDate.value.toISOString().split('T')[0]); // Format YYYY-MM-DD
};

// Price for outside MoD legend (depends on delivery point)
const outsideMoDPrice = computed(() => {
  return props.selectedDeliveryPoint === 'address' || cartStore.hasDisabledOutsideModFee ? useLocalizedCurrency(0, true) : useLocalizedCurrency(globalContent.outsideModDeliveryFeePrice as number, true);
});

</script>
<style scoped>

:deep(.dp__calendar) {
  font-family: "Karla", "Helvetica Neue", sans-serif;
  font-size: 14px;
  font-weight: 500;
}

:deep(.dp--future) {
  color: #2C573D;
  background: #E6F5EC;

  &:hover:not(.dp__cell_disabled, .dp__active_date) {
    color: #FFFFFF;
    background: #548A69;
  }
}

:deep(.dp__cell_disabled) {
  color: #8A8681;
  background: #FAF5EF;
}

:deep(.dp__active_date) {
  color: #FFFFFF;
  background: #2C573D;
  outline-width: 2px;
  outline-color: #2C573D;
  outline-style: solid;
  outline-offset: 1px;
}

:deep(.dp--future):has(.day-outside-mod) {
  color: #572C2C;
  background: #F5E6E6;

  &:hover:not(.dp__cell_disabled, .dp__active_date) {
    color: #FFFFFF;
    background: #A35353;
  }
}

:deep(.dp__active_date):has(.day-outside-mod) {
  color: #FFFFFF;
  background: #572C2C;
  outline-color: #572C2C;
}

:deep(.dp__calendar_row) {
  @apply my-24 first:mt-12;
  margin: 24px 0;
  gap: 12px;
}

:deep(.dp__calendar_item) {
  position: relative;
}

:deep(.dp__today) {
  border-color: #547E8A;
}

:deep(.dp__calendar_header) {
  @apply uppercase font-karla text-text-subtle font-medium tracking-wide;
}

:deep(.dp__action_button) {
  font-family: "Karla", "Helvetica Neue", sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 2px 8px;
  height: auto;
}

:deep(.dp__action_select) {
  background: #2e2d2b;
  &:hover {
    background: #4D4A48;
  }
}

:deep(.dp__outer_menu_wrap) {
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
</style>
